// client/src/coms/components/StickerUploader.js
// Add stickers to a pack: drag and drop or browse (several at once), preview, name, keywords, fit/crop,
// per-file upload progress. Each file is prepared in the browser (resize; animated GIF/WebP sent unchanged)
// and validated again and re-encoded by the server, which stores identical images only once.
// uploadOne(item, preparedFile, onProgress) replaces the server upload (local packs save on this device).
import React, { useEffect, useRef, useState } from 'react';
import { isProbablyAnimated, prepareImageFile, uploadWithProgress } from '../imagePrep';
import KeywordTagInput from './KeywordTagInput';

const ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';
const MAX_SOURCE_BYTES = 8 * 1024 * 1024; // before preparation; the prepared file must be under 2 MB
const field = { background: 'var(--vxs-1, #111214)', color: 'var(--vx-text, #fff)', border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))', borderRadius: 8, padding: '6px 8px', fontSize: 13 };

let nextId = 1;

function serverUpload(pack) {
  return (it, prepared, onProgress) =>
    uploadWithProgress(`/stickers/packs/${pack.id}/stickers`, { name: it.name.trim() || 'sticker', keywords: it.keywords.join(',') }, prepared, onProgress);
}

export default function StickerUploader({ pack, onUploaded, onClose, roomLeft = 60, uploadOne = null, destination = '' }) {
  const [items, setItems] = useState([]); // { id, file, preview, name, keywords, progress, status, error }
  const [mode, setMode] = useState('fit');
  const [dragging, setDragging] = useState(false);
  const [running, setRunning] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => () => items.forEach((i) => URL.revokeObjectURL(i.preview)), []); // eslint-disable-line react-hooks/exhaustive-deps

  function addFiles(list) {
    const files = Array.from(list || []);
    const next = [];
    for (const file of files) {
      const ok = /^image\/(png|jpeg|gif|webp)$/.test(file.type);
      next.push({
        id: nextId++,
        file,
        preview: URL.createObjectURL(file),
        name: (file.name || 'sticker').replace(/\.[^.]+$/, '').slice(0, 40),
        keywords: [],
        progress: 0,
        status: ok && file.size <= MAX_SOURCE_BYTES ? 'ready' : 'error',
        error: !ok ? 'Only PNG, JPEG, GIF or WebP images' : file.size > MAX_SOURCE_BYTES ? 'This file is too large (max 8 MB before resizing)' : '',
      });
    }
    setItems((cur) => [...cur, ...next]);
  }

  const update = (id, patch) => setItems((cur) => cur.map((i) => (i.id === id ? { ...i, ...patch } : i)));

  function remove(id) {
    setItems((cur) => {
      const it = cur.find((i) => i.id === id);
      if (it) URL.revokeObjectURL(it.preview);
      return cur.filter((i) => i.id !== id);
    });
  }

  async function uploadAll() {
    setRunning(true);
    let uploaded = 0;
    for (const it of items) {
      if (it.status !== 'ready' && it.status !== 'failed') continue;
      if (uploaded >= roomLeft) {
        update(it.id, { status: 'failed', error: 'The pack is full (60 stickers)' });
        continue;
      }
      update(it.id, { status: 'uploading', progress: 0, error: '' });
      try {
        const prepared = isProbablyAnimated(it.file) ? it.file : await prepareImageFile(it.file, { size: 320, mode });
        if (prepared.size > 2 * 1024 * 1024) throw new Error('This image is too large (max 2 MB). Animated images are kept as they are, so use a smaller file.');
        await (uploadOne || serverUpload(pack))(it, prepared, (p) => update(it.id, { progress: p }));
        uploaded += 1;
        update(it.id, { status: 'done', progress: 1 });
      } catch (e) {
        update(it.id, { status: 'failed', error: String((e && e.data && e.data.error) || (e && e.message) || 'Upload failed') });
      }
    }
    setRunning(false);
    setItems((cur) => {
      cur.filter((i) => i.status === 'done').forEach((i) => URL.revokeObjectURL(i.preview));
      return cur.filter((i) => i.status !== 'done');
    });
    if (uploaded && typeof onUploaded === 'function') onUploaded();
  }

  const pending = items.filter((i) => i.status === 'ready' || i.status === 'failed').length;

  return (
    <div className="vox-uploader">
      <div
        className={`vox-dropzone ${dragging ? 'over' : ''}`}
        role="button"
        tabIndex={0}
        aria-label={`Add sticker images to ${pack.name}: drop files here or press Enter to browse`}
        onClick={() => inputRef.current && inputRef.current.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && inputRef.current) {
            e.preventDefault();
            inputRef.current.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          addFiles(e.dataTransfer && e.dataTransfer.files);
        }}
      >
        <div style={{ fontWeight: 800 }}>Drop images here or click to browse</div>
        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>PNG, JPEG, GIF or WebP · transparency and animation are kept · up to {roomLeft} more in this pack{destination ? ` · ${destination}` : ''}</div>
        <input ref={inputRef} type="file" multiple accept={ACCEPT} style={{ display: 'none' }} aria-label="Choose sticker images" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
      </div>

      {items.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          {items.map((it) => (
            <div key={it.id} className="vox-upload-row">
              <img src={it.preview} alt="" style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 8, background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))', flex: '0 0 auto' }} />
              <div style={{ flex: 1, minWidth: 180, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <input value={it.name} onChange={(e) => update(it.id, { name: e.target.value })} maxLength={40} aria-label="Sticker name" placeholder="Sticker name" disabled={it.status === 'uploading'} style={field} />
                <KeywordTagInput label={`Keywords for ${it.name || 'sticker'}`} value={it.keywords} onChange={(k) => update(it.id, { keywords: k })} disabled={it.status === 'uploading'} placeholder="Keywords (optional)" />
                {it.status === 'uploading' ? (
                  <div className="vox-progress" role="progressbar" aria-label={`Uploading ${it.name}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(it.progress * 100)}>
                    <div style={{ width: `${Math.round(it.progress * 100)}%` }} />
                  </div>
                ) : null}
                {it.error ? <div className="coms-form-error" style={{ fontSize: 12 }}>{it.error}</div> : null}
              </div>
              <button type="button" className="vox-sticker-tile-btn" style={{ position: 'static' }} aria-label={`Remove ${it.name} from the upload list`} disabled={it.status === 'uploading'} onClick={() => remove(it.id)}>
                ✕
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
        <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
          Fitting:
          <select value={mode} onChange={(e) => setMode(e.target.value)} style={{ ...field, padding: '4px 6px' }}>
            <option value="fit">Fit whole image</option>
            <option value="crop">Crop to square</option>
          </select>
        </label>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button type="button" className="coms-mini-btn" onClick={onClose} disabled={running}>
            Close
          </button>
          <button type="button" className="coms-mini-btn primary" onClick={uploadAll} disabled={running || !pending}>
            {running ? (uploadOne ? 'Saving…' : 'Uploading…') : `${uploadOne ? 'Save' : 'Upload'} ${pending || ''} sticker${pending === 1 ? '' : 's'}`.replace('  ', ' ')}
          </button>
        </span>
      </div>
    </div>
  );
}
